import { Lightbulb } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DemoPreview } from "@/src/features/demo-contracts/types";

/* ─── Severity ──────────────────────────────────────────────────────────── */

function severityMeta(score: number): {
  label: string;
  toneClassName: string;
  barClassName: string;
} {
  if (score >= 80) {
    return {
      label: "Critical",
      toneClassName: "border-rose-400/30 bg-rose-400/10 text-rose-300",
      barClassName: "bg-rose-400/80",
    };
  }

  if (score >= 60) {
    return {
      label: "High",
      toneClassName: "border-orange-400/30 bg-orange-400/10 text-orange-300",
      barClassName: "bg-orange-400/80",
    };
  }

  if (score >= 40) {
    return {
      label: "Moderate",
      toneClassName: "border-amber-400/30 bg-amber-400/10 text-amber-300",
      barClassName: "bg-amber-400/80",
    };
  }

  return {
    label: "Low",
    toneClassName: "border-white/15 bg-white/5 text-primary-foreground/50",
    barClassName: "bg-white/30",
  };
}

/* ─── Derived helpers ─────────────────────────────────────────────────────── */

function deriveTopPainPoints(preview: DemoPreview): DemoPreview["painPoints"] {
  return [...preview.painPoints]
    .sort((painPointA, painPointB) => {
      if (painPointB.severityScore !== painPointA.severityScore) {
        return painPointB.severityScore - painPointA.severityScore;
      }

      return painPointB.frequency - painPointA.frequency;
    })
    .slice(0, 3);
}

function formatCategoryLabel(category: string): string {
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function deriveFeatureToPainMapping(
  preview: DemoPreview,
): Array<{ ideaTitle: string; mappedPains: string[] }> {
  if (!preview.generatedIdeas || preview.generatedIdeas.length === 0) {
    return [];
  }

  return preview.generatedIdeas.slice(0, 3).map((idea) => {
    const solutionText = [
      idea.solutionApproach ?? "",
      idea.problemItSolves ?? "",
      idea.description ?? "",
      idea.whyNow ?? "",
    ]
      .join(" ")
      .toLowerCase();

    const matchedPains = preview.painPoints
      .filter((painPoint) => {
        const painKeywords = [
          painPoint.title.toLowerCase(),
          painPoint.description.toLowerCase(),
          painPoint.category,
        ].join(" ");
        return (
          solutionText.includes(
            painPoint.title.split(" ")[0]?.toLowerCase() ?? "",
          ) ||
          painKeywords
            .split(" ")
            .some((word) => word.length > 4 && solutionText.includes(word))
        );
      })
      .slice(0, 2)
      .map((pp) => pp.title);

    return {
      ideaTitle: idea.title,
      mappedPains:
        matchedPains.length > 0
          ? matchedPains
          : [preview.painPoints[0]?.title].filter(Boolean),
    };
  });
}

export function deriveIntensityExplanation(
  painPoint: DemoPreview["painPoints"][number],
): string | null {
  const { severityScore, frequency } = painPoint;

  if (severityScore >= 75 && frequency < 30) {
    return `Critical signal affecting a focused group — severity is high but fewer people are discussing it publicly right now.`;
  }

  if (frequency >= 100 && severityScore < 55) {
    return `Frequent chatter but lower urgency — this is a background annoyance rather than an acute blocker for most users.`;
  }

  if (frequency < 30 && severityScore >= 45) {
    return `Low mention volume now but high impact — could indicate an emerging pain point before it becomes mainstream.`;
  }

  if (severityScore >= 50 && frequency >= 50) {
    return `Consistent pain with solid evidence — this is a mature friction point with reliable quote coverage.`;
  }

  return null;
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function IdeaFeatureMapping({ preview }: { preview: DemoPreview }) {
  const mapping = deriveFeatureToPainMapping(preview);
  if (mapping.length === 0) return null;

  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
        Why these ideas fit the evidence
      </p>
      <div className="mt-2 space-y-1.5">
        {mapping.map(({ ideaTitle, mappedPains }) => (
          <div
            key={ideaTitle}
            className="rounded-lg border border-white/10 bg-black/20 px-3 py-2.5"
          >
            <p className="font-mono text-[11px] text-primary-foreground/80">
              {ideaTitle}
            </p>
            <p className="mt-0.5 font-mono text-[10px] text-primary-foreground/40">
              Most connected to: {mappedPains.join(", ")}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SeverityContextNote({
  topPainPoints,
}: {
  topPainPoints: DemoPreview["painPoints"];
}) {
  const explanations = topPainPoints
    .map((pp) => deriveIntensityExplanation(pp))
    .filter((e): e is string => e !== null);

  if (explanations.length === 0) return null;

  return (
    <div className="rounded-xl border border-accent/20 bg-accent/5 p-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-amber-300/70">
        How to read lower-volume signals
      </p>
      <div className="mt-1.5 space-y-1">
        {explanations.slice(0, 2).map((note, i) => (
          <p
            key={i}
            className="font-mono text-[10px] leading-[1.6] text-primary-foreground/55"
          >
            {note}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ─── Overview section ──────────────────────────────────────────────────── */

export function OverviewSection({
  preview,
  selectedIdeaId,
  onSelectIdea,
}: {
  preview: DemoPreview;
  selectedIdeaId?: string;
  onSelectIdea: (ideaId: string) => void;
}) {
  const topPainPoints = deriveTopPainPoints(preview);
  const selectedIdea =
    preview.generatedIdeas?.find((idea) => idea.id === selectedIdeaId) ??
    preview.generatedIdeas?.[0];

  return (
    <>
      {/* Generated ideas */}
      {preview.generatedIdeas && preview.generatedIdeas.length > 0 ? (
        <section className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
            Best-fit ideas
          </p>
          <p className="font-mono text-[12px] leading-[1.7] text-primary-foreground/60 sm:text-[13px]">
            These concepts are the closest match to the pain patterns surfaced
            in this report.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {preview.generatedIdeas.map((idea) => (
              <button
                key={idea.id}
                type="button"
                onClick={() => onSelectIdea(idea.id)}
                className={cn(
                  "rounded-md border px-3 py-2 text-left transition",
                  selectedIdea?.id === idea.id
                    ? "border-accent/40 bg-accent/10"
                    : "border-white/10 bg-black/20 hover:bg-white/[0.05]",
                )}
              >
                <p className="font-mono text-[12px] text-primary-foreground sm:text-[13px]">
                  {idea.title}
                </p>
                <p className="mt-1 font-mono text-[11px] text-primary-foreground/58">
                  Fit {idea.fitScore}%
                </p>
              </button>
            ))}
          </div>
          {selectedIdea && (
            <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-3">
              <div className="flex items-start gap-2">
                <Lightbulb
                  className="mt-0.5 size-3.5 shrink-0 text-amber-400/70"
                  aria-hidden="true"
                />
                <div className="space-y-1.5">
                  <p className="font-mono text-[12px] leading-[1.8] text-primary-foreground/76 sm:text-[13px]">
                    {selectedIdea.whyNow}
                  </p>
                  {selectedIdea.problemItSolves && (
                    <p className="font-mono text-[11px] leading-[1.7] text-primary-foreground/60">
                      Problem this idea tackles: {selectedIdea.problemItSolves}
                    </p>
                  )}
                  {selectedIdea.solutionApproach && (
                    <p className="font-mono text-[11px] leading-[1.7] text-primary-foreground/60">
                      Suggested approach: {selectedIdea.solutionApproach}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
            Best-fit ideas
          </p>
          <p className="mt-2 font-mono text-[12px] leading-[1.7] text-primary-foreground/60">
            Live analysis adds idea recommendations once enough evidence is
            available.
          </p>
        </section>
      )}

      <IdeaFeatureMapping preview={preview} />

      {/* Top pain points */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
            Top pain signals to understand first
          </p>
          <div className="h-px flex-1 bg-border" />
          <Badge
            variant="outline"
            className="font-mono border-white/10 bg-white/5 text-[10px] text-primary-foreground/50"
          >
            {topPainPoints.length}
          </Badge>
        </div>

        <div className="divide-y divide-border rounded-xl border border-border bg-white/[0.03]">
          {topPainPoints.map((painPoint) => {
            const severity = severityMeta(painPoint.severityScore);

            return (
              <article key={painPoint.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <p className="font-serif text-[17px] font-semibold leading-tight text-primary-foreground sm:text-[18px]">
                      {painPoint.title}
                    </p>
                    <p className="font-mono text-[12px] leading-[1.8] text-primary-foreground/72 sm:text-[13px]">
                      {painPoint.description}
                    </p>
                  </div>

                  <Badge
                    variant="outline"
                    className={cn(
                      "shrink-0 rounded-md px-2 py-1 font-mono text-[10px] uppercase tracking-widest",
                      severity.toneClassName,
                    )}
                  >
                    {severity.label}
                  </Badge>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <Badge
                    variant="outline"
                    className="rounded-md border-white/15 bg-white/5 font-mono text-[11px] text-primary-foreground/65"
                  >
                    {formatCategoryLabel(painPoint.category)}
                  </Badge>

                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/30">
                      Intensity
                    </span>
                    <div className="h-1.5 w-28 rounded-full bg-white/10">
                      <div
                        className={cn(
                          "h-full rounded-full",
                          severity.barClassName,
                        )}
                        style={{
                          width: `${Math.min(100, painPoint.severityScore)}%`,
                        }}
                      />
                    </div>
                  </div>

                  <span className="font-mono text-[10px] text-primary-foreground/30">
                    {painPoint.frequency} mentions
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <SeverityContextNote topPainPoints={topPainPoints} />
    </>
  );
}
